import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  MessageCircle, Mail, Instagram, Clock, HelpCircle,
  CreditCard, Briefcase, Headphones,
} from "lucide-react";
import { LegalFooter } from "./privacy-policy";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const CHANNELS = [
  {
    icon: MessageCircle,
    title: "WhatsApp",
    description: "Send us a message anytime. We handle WhatsApp support on business days (Mon–Sat, 8am–8pm WAT).",
    action: "Message us",
    href: "https://wa.me/2349040460813?text=Hi%2C%20I%20need%20help%20with%20RetailPulse",
    badge: "Fastest",
    badgeColor: "bg-emerald-100 text-emerald-800",
    highlight: true,
  },
  {
    icon: Mail,
    title: "Email",
    description: "For detailed questions, billing issues, or formal correspondence. We reply on business days.",
    action: "Send an email",
    href: "mailto:support@retailpulsepos.com",
    badge: "Email support",
    badgeColor: "bg-blue-100 text-blue-800",
    highlight: false,
  },
  {
    icon: Instagram,
    title: "Instagram",
    description: "Follow us for product updates, retail tips, and feature announcements. DMs are welcome too.",
    action: "Follow @retailpulsepos",
    href: "https://instagram.com/retailpulsepos",
    badge: "Updates & tips",
    badgeColor: "bg-pink-100 text-pink-800",
    highlight: false,
  },
];

const TOPICS = [
  {
    icon: HelpCircle,
    title: "General help",
    description: "Questions about how to use the app.",
    href: `${BASE}/help`,
    cta: "Visit Help Centre",
    internal: true,
  },
  {
    icon: CreditCard,
    title: "Billing & subscriptions",
    description: "Payment issues, plan changes, or refund requests.",
    href: "https://wa.me/2349040460813?text=Hi%2C%20I%20have%20a%20billing%20question",
    cta: "Message us",
    internal: false,
  },
  {
    icon: Briefcase,
    title: "Careers",
    description: "Interested in joining the RetailPulse team.",
    href: `${BASE}/careers`,
    cta: "See open roles",
    internal: true,
  },
  {
    icon: Headphones,
    title: "Enterprise support",
    description: "Priority support for Enterprise plan customers.",
    href: "https://wa.me/2349040460813?text=Hi%2C%20I%20am%20an%20Enterprise%20customer",
    cta: "Priority line",
    internal: false,
  },
];

export default function Contact() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href={`${BASE}/`}>
            <span className="font-bold text-lg text-primary cursor-pointer">RetailPulse</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href={`${BASE}/login`}><Button variant="ghost" size="sm">Sign in</Button></Link>
            <Link href={`${BASE}/signup`}><Button size="sm">Get started free</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b bg-gradient-to-br from-primary/5 via-background to-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
            <Headphones className="w-3 h-3 mr-1" /> Contact us
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight mb-4">We're here to help</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto leading-relaxed">
            Whether you have a question, a problem, or feedback — reach out and we'll get back to you as fast as we can.
          </p>
        </div>
      </section>

      {/* Response time banner */}
      <div className="bg-emerald-50 border-b border-emerald-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-center gap-2 text-sm text-emerald-800">
          <Clock className="w-4 h-4 text-emerald-600" />
          <span>WhatsApp support hours: <strong>Mon–Sat, 8am–8pm WAT</strong>. Messages outside these hours may wait until the next business day.</span>
        </div>
      </div>

      {/* Contact channels */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-2xl font-bold mb-2">Get in touch</h2>
        <p className="text-muted-foreground mb-8">Choose the channel that works best for you.</p>

        <div className="grid sm:grid-cols-3 gap-6">
          {CHANNELS.map(ch => {
            const Icon = ch.icon;
            return (
              <Card key={ch.title} className={`relative overflow-hidden ${ch.highlight ? "border-primary shadow-sm" : ""}`}>
                {ch.highlight && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary rounded-t-xl" />
                )}
                <CardContent className="pt-6 pb-6 flex flex-col gap-4 h-full">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{ch.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border-0 ${ch.badgeColor}`}>{ch.badge}</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{ch.description}</p>
                  </div>
                  <a href={ch.href} target="_blank" rel="noopener noreferrer">
                    <Button variant={ch.highlight ? "default" : "outline"} size="sm" className="w-full gap-2">
                      <Icon className="w-3.5 h-3.5" />
                      {ch.action}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Topic-based routing */}
      <section className="border-t bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="text-2xl font-bold mb-2">What do you need help with?</h2>
          <p className="text-muted-foreground mb-8">Go straight to the right place.</p>

          <div className="grid sm:grid-cols-2 gap-4">
            {TOPICS.map(topic => {
              const Icon = topic.icon;
              return (
                <div key={topic.title} className="flex items-start gap-4 p-5 rounded-xl border bg-background hover:border-primary/30 hover:bg-primary/5 transition-colors">
                  <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm mb-1">{topic.title}</h3>
                    <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{topic.description}</p>
                    {topic.internal ? (
                      <Link href={topic.href}>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-primary px-0 hover:bg-transparent hover:underline">
                          {topic.cta} →
                        </Button>
                      </Link>
                    ) : (
                      <a href={topic.href} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-primary px-0 hover:bg-transparent hover:underline">
                          {topic.cta} →
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}
